/**
 * Provides AppInsights methods for tracking events
 */
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { IPageViewTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import {
	PropsWithChildren,
	createContext,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import { useFeatureFlag } from '../../configcat/useFeatureFlag';
import { useLocalization } from '../../i18n/LocalizationProvider';
import { useCurrentPageInfo } from '../CurrentPageInfoProvider';
import { InsightsEvent } from './appInsightsEvents';

export { useAppInsightsContext };

export interface AppInsights {
	init: (disabled: boolean) => void;
	trackEvent: (event: InsightsEvent) => void;
	trackException: (error: Error | string, data?: Record<string, any>) => void;
	trackPageView: (name: string, properties: IPageViewTelemetry['properties']) => void;
}

export const AppInsightsContext = createContext<AppInsights>({
	init: () => {},
	trackEvent: () => {},
	trackException: () => {},
	trackPageView: () => {},
});

/**
 * Provides methods to send AppInsights analytic events
 *
 * Analytics is managed through configuration (see ConfigCatFlagKey.DisableAppInsightsAnalytics) and can be disabled.
 * Since configuration retrieval is asynchronous, and we need to begin tracking events on page load (such as PageView events),
 * we collect and queue events triggered before initialization, then send them once analytics is enabled.
 */
export const AppInsightsProvider = memo(({ children }: PropsWithChildren) => {
	const appInsights = useAppInsightsContext();
	const { getCurrentPageInfo } = useCurrentPageInfo();
	const { locale } = useLocalization();

	const isInitialized = useRef<boolean>(false);
	const isDisabled = useRef<boolean>(true);
	const queue = useRef<VoidFunction[]>([]);

	const init = useCallback<AppInsights['init']>((disabled) => {
		isDisabled.current = disabled;

		const wasInitialized = isInitialized.current;
		isInitialized.current = true;
		if (wasInitialized === false) {
			if (disabled) {
				queue.current = [];
			} else {
				while (queue.current.length) {
					queue.current.shift()?.();
				}
			}
		}
	}, []);

	const trackEvent = useCallback<AppInsights['trackEvent']>(
		(event) => {
			if (isInitialized.current === false) {
				queue.current.push(() => trackEvent(event));
			}

			if (isDisabled.current) return;

			if (appInsights) {
				const properties = {
					...getCurrentPageInfo(),
					locale,
					...event,
				};
				appInsights.trackEvent({ name: event.name }, properties);
			}
		},
		[appInsights, getCurrentPageInfo, locale],
	);

	const trackException = useCallback<AppInsights['trackException']>(
		(error, data = {}) => {
			if (isInitialized.current === false) {
				queue.current.push(() => trackException(error, data));
			}

			if (isDisabled.current) return;

			const exception = typeof error === 'string' ? new Error(error) : error;

			if (appInsights) {
				appInsights.trackException({
					exception,
					severityLevel: SeverityLevel.Error,
					properties: {
						...getCurrentPageInfo(),
						...data,
					},
				});
			}
		},
		[appInsights, getCurrentPageInfo],
	);

	const trackPageView = useCallback<AppInsights['trackPageView']>(
		(name: string, properties: IPageViewTelemetry['properties']) => {
			if (isInitialized.current === false) {
				queue.current.push(() => trackPageView(name, properties));
			}

			if (isDisabled.current) return;

			appInsights.trackPageView({ name, properties });
		},
		[appInsights],
	);

	const value = useMemo<AppInsights>(() => {
		return {
			init,
			trackEvent,
			trackException,
			trackPageView,
		};
	}, [init, trackEvent, trackException, trackPageView]);

	const [disableAppInsightsAnalytics] = useFeatureFlag('disableAppInsightsAnalytics');
	useEffect(() => {
		if (disableAppInsightsAnalytics !== undefined) init(!!disableAppInsightsAnalytics);
	}, [disableAppInsightsAnalytics, init]);

	return <AppInsightsContext value={value}>{children}</AppInsightsContext>;
});

AppInsightsContext.displayName = 'AppInsightsContext';
